<template>
  <transition name="van-slide-up" appear @afterEnter="afterEnter">
    <div
      ref="messageItem"
      class="message-item"
      :data-support-order-message-id="messageId || undefined"
      @click="handleATagJump"
    >
      <div v-if="isService" class="message-item-service">
        <em class="icon iconfont icon-service"></em>
        <div class="cont">
          <OrderReferenceQuote
            :reference-msg="referenceMsg"
            :is-service="true"
            @scroll-to-message="id => $emit('scroll-to-message', id)"
          />
          <p
            class="cont-msg"
            v-html="isAiBot ? showAiBotMessage : decorateServiceMsg(msg)"
            @click="handleAiBotMsgClick"
          ></p>

          <div v-if="getImgAndVideoSequence.length > 0" class="cont-img">
            <span
              v-for="(item, index) in getImgAndVideoSequence"
              :key="mediaItemKey(item, index)"
              class="cont-wrap"
            >
              <video
                v-if="item.type === 'video' && getMediaVideoSrc(item)"
                class="cont-video"
                :src="getMediaVideoSrc(item)"
                :poster="item.url || undefined"
                controls
                playsinline
                webkit-playsinline
                preload="metadata"
                @click.stop
              />
              <van-image
                v-else
                radius="6px"
                width="58px"
                height="58px"
                fit="cover"
                :show-loading="true"
                lazy-load
                :src="item.url"
                @click.stop="previewMedia(item)"
              >
                <template v-slot:loading>
                  <van-loading type="spinner" size="20" />
                </template>
              </van-image>
            </span>
          </div>
        </div>
      </div>
      <div v-else class="message-item-player">
        <div class="cont">
          <template v-if="orderId && title">
            <p class="cont-id">[ NO.{{ showOrderId }} ]</p>
            <p class="cont-title">{{ title }}</p>
          </template>
          <OrderReferenceQuote
            :reference-msg="referenceMsg"
            :is-service="false"
            @scroll-to-message="id => $emit('scroll-to-message', id)"
          />
          <p
            class="cont-msg"
            v-html="isAiBot ? showAiBotMessage : msg"
            @click="handleAiBotMsgClick"
          ></p>
          <div v-if="getImgAndVideoSequence.length > 0" class="cont-img">
            <span
              v-for="(item, index) in getImgAndVideoSequence"
              :key="mediaItemKey(item, index)"
              class="cont-wrap"
            >
              <video
                v-if="item.type === 'video' && getMediaVideoSrc(item)"
                class="cont-video cont-img-item"
                :src="getMediaVideoSrc(item)"
                :poster="item.url || undefined"
                controls
                playsinline
                webkit-playsinline
                preload="metadata"
                @click.stop
              />
              <van-image
                v-else
                class="cont-img-item"
                radius="6px"
                width="58px"
                height="58px"
                fit="cover"
                :show-loading="true"
                lazy-load
                :src="item.url"
                @click.stop="previewMedia(item)"
              >
                <template v-slot:loading>
                  <van-loading type="spinner" size="20" />
                </template>
              </van-image>
            </span>
          </div>
        </div>
        <em class="icon iconfont icon-player"></em>
      </div>
    </div>
  </transition>
</template>

<script>
import { ImagePreview } from 'vant';
import bridge from '@utiles/bridge';
import { buildMessageMediaSequence } from '@utiles/officialSupport/messageMedia';
import OrderReferenceQuote from '@components/officialSupport/OrderReferenceQuote.vue';

export default {
  name: 'MessageItem',
  components: {
    OrderReferenceQuote
  },
  props: {
    messageId: {
      type: [String, Number],
      default: ''
    },
    referenceMsg: {
      type: Object,
      default: null
    },
    isService: {
      type: Boolean,
      default: false
    },
    orderId: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      default: ''
    },
    msg: {
      type: String,
      default: ''
    },
    imgList: {
      type: Array,
      default: () => []
    },
    videoList: {
      type: String,
      default: ''
    },
    videoDurations: {
      type: String,
      default: ''
    },
    isLastMsg: {
      type: Boolean,
      default: false
    },
    videoPreviewPic: {
      type: Array,
      default: () => []
    },
    picAndVideoSequence: {
      type: String,
      default: ''
    },
    isAiBot: {
      type: Boolean,
      default: false
    },
    aiBotMessage: {
      type: Object,
      default: () => {}
    },
    isSupportCreateAibotOrder: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    getImgAndVideoSequence() {
      return buildMessageMediaSequence({
        screenPic: this.imgList,
        videoList: this.videoList,
        videoDurations: this.videoDurations,
        videoPreviewPic: this.videoPreviewPic,
        picAndVideoSequence: this.picAndVideoSequence
      });
    },
    showOrderId() {
      if (this.isSupportCreateAibotOrder) {
        return `A${this.orderId}`;
      }
      return this.orderId;
    },
    showAiBotMessage() {
      if (this.isAiBot) {
        return this.replaceMarkdownImagesWithHtml(
          this.mixImageInContent({
            content: this.aiBotMessage?.content || '',
            message_files: this.aiBotMessage?.message_files || []
          })
        );
      }
      return '';
    }
  },
  methods: {
    afterEnter(el) {
      setTimeout(() => {
        if (this.isLastMsg) {
          el &&
            el?.scrollIntoView({
              behavior: 'smooth'
            });
        }
      }, 100);
    },
    mediaItemKey(item, index) {
      return `${item.type || 'image'}|${item.url || ''}|${item.videoPath || ''}|${index}`;
    },
    getMediaVideoSrc(item) {
      return String(item?.videoPath || item?.url || '').trim();
    },
    previewMedia(item) {
      if (!item) {
        return;
      }
      if (item.type === 'video') {
        return;
      }
      const images = this.getImgAndVideoSequence
        .filter(row => row.type === 'image' && row.url)
        .map(row => row.url);
      if (!images.length) {
        return;
      }
      const start = images.indexOf(item.url);
      ImagePreview({
        images,
        startPosition: start >= 0 ? start : 0,
        closeable: true
      });
    },
    decorateServiceMsg(msg) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(msg, 'text/html');
      const links = doc.querySelectorAll('a');

      links.forEach(link => {
        link.removeAttribute('target');
        const hrefValue = link.getAttribute('href');
        link.setAttribute('data-url', hrefValue);
        link.setAttribute('href', 'javascript:;');
      });
      return doc.body.innerHTML;
    },
    handleATagJump(e) {
      const dataset = e?.target?.dataset;
      const tagName = e?.target?.tagName;
      if (tagName === 'A' && dataset?.url) {
        bridge.runInsdk()
          ? bridge.callSdkMethod('openWebView', {
              url: dataset.url,
              loginDir: 0,
              appendParams: 1
            })
          : window.open(dataset.url, '_blank');
      }
    },
    handleAiBotMsgClick(e) {
      const target = e.target.closest('.j-question-screenshot-detail-page');
      if (target) {
        ImagePreview({
          images: [target.src],
          startPosition: 0,
          closeable: true
        });
      }
    },
    mixImageInContent({ content, message_files }) {
      if (!content) return '';
      if (!message_files || !message_files?.length) return content;
      return `${content}\n${message_files
        .map(item => `![screenshot](${item.url})`)
        ?.join('\n')}`;
    },
    replaceMarkdownImagesWithHtml(markdownText) {
      const regex = /!\[([^\]]*)\]\(([^)]*)\)/g;
      return markdownText.replace(regex, (_, altText, imageUrl) => {
        return `<img src="${imageUrl}" alt="${altText}" style="width: auto; height: 100px;margin-right: 10px" class="j-question-screenshot-detail-page" />`;
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.message-item {
  box-sizing: border-box;
  width: 100%;
  margin: 0 auto 18px;
  &-service,
  &-player {
    display: flex;
    align-items: flex-start;

    .icon {
      font-size: 24px;
      color: var(--main-color);
    }
    .cont {
      box-sizing: border-box;
      min-width: 120px;
      max-width: 100%;
      padding: 10px;
      font-size: 16px;
      line-height: 21px;

      &-msg {
        word-wrap: break-word;
        -webkit-user-select: text;
        user-select: text;
        white-space: pre-wrap;
      }
      &-img {
        margin-top: 10px;
        margin-right: -8px;

        .van-image {
          margin: 0 8px 8px 0;
        }

        &-item {
          margin: 5px;
        }
      }
      &-video {
        display: block;
        max-width: 100%;
        width: 200px;
        height: auto;
        margin: 0 8px 8px 0;
        border-radius: 6px;
        vertical-align: top;
        background-color: #000;
      }
      &-wrap {
        position: relative;
        display: inline-block;
      }
    }
  }

  &-service {
    justify-content: flex-start;
    .icon {
      margin-right: 16px;
    }
    .cont {
      background-color: var(--main-color);
      border-radius: 6px 6px 30px 6px;
      color: var(--text-on-main-bg-color);
    }
  }
  &-player {
    justify-content: flex-end;
    .icon {
      margin-left: 16px;
    }
    .cont {
      background-color: var(--block-bg-light-color);
      border-radius: 6px 6px 6px 30px;

      &-id {
        font-size: 12px;
        color: var(--placeholder-color);
      }
      &-title {
        margin-bottom: 8px;
        padding-bottom: 8px;
        font-weight: bold;
        font-size: 16px;
        line-height: 22px;
        border-bottom: 1px dotted var(--border-color);
      }
    }
  }
}
</style>
<style lang="scss">
.cont-msg {
  a {
    color: var(--text-on-main-bg-color);
    text-decoration: underline;
    text-underline-offset: 0.2em;
  }
  strong {
    font-weight: bold;
  }
  em {
    font-style: italic;
  }
}
</style>
